import { getLocale, resultsPl, runSearch } from "./algolia-client.js";
import { cleanClone, fillCard, readTemplatesFrom } from "./hit-mapping.js";

const HITS_PER_PAGE = 12;
const DEBOUNCE_MS = 180;

const I18N = {
  en: {
    results: (n) => (n === 1 ? "1 result" : `${n.toLocaleString("en")} results`),
    loadMore: "Load more",
    learnMore: "Learn More",
    prompt: "Start typing to search across products, solutions, case studies and insights.",
    promptTitle: "What are you looking for?",
    emptyTitle: "No results found",
    emptyBody: "Try adjusting your search terms or using more general keywords.",
  },
  ua: {
    results: (n) => `${n.toLocaleString("uk")} результатів`,
    loadMore: "Показати ще",
    learnMore: "Детальніше",
    prompt: "Почніть вводити запит, щоб шукати серед продуктів, рішень, кейсів та новин.",
    promptTitle: "Що ви шукаєте?",
    emptyTitle: "Нічого не знайдено",
    emptyBody: "Спробуйте змінити запит або використати загальніші ключові слова.",
  },
  pl: {
    results: resultsPl,
    loadMore: "Pokaż więcej",
    learnMore: "Dowiedz się więcej",
    prompt: "Zacznij pisać, aby szukać wśród produktów, rozwiązań, case studies i aktualności.",
    promptTitle: "Czego szukasz?",
    emptyTitle: "Nie znaleziono wyników",
    emptyBody: "Spróbuj zmienić zapytanie lub użyć bardziej ogólnych słów kluczowych.",
  },
};

// The navbar ships its own .u-example and .search-result-wrap, and they precede the page
// copies in the DOM. Never resolve these with a bare document query.
function findOutsideNavbar(selector) {
  return Array.prototype.find.call(
    document.querySelectorAll(selector),
    (element) => !element.closest(".navbar")
  );
}

export function initSearchPage({ search = runSearch } = {}) {
  const input = document.querySelector("#search");
  const resultsRoot = findOutsideNavbar(".search-result-wrap");
  if (!input || !resultsRoot) return null;

  const { card, button } = readTemplatesFrom(findOutsideNavbar(".u-example"));
  if (!card || !button) {
    console.warn(
      "[mx-search] /search needs a .u-example block with a .search-result card and an " +
        ".elem__button-wrap button outside the navbar.",
      { card: !!card, button: !!button }
    );
    return null;
  }

  const locale = getLocale();
  const t = I18N[locale] || I18N.en;

  const form = input.closest("form");
  if (form) {
    form.setAttribute("novalidate", "");
    form.addEventListener("submit", (event) => event.preventDefault());
  }

  const emptyWrap = resultsRoot.querySelector(".searc__empty-wrap");
  const emptyTitle = emptyWrap && emptyWrap.querySelector("h2");
  const emptyBody = emptyWrap && emptyWrap.querySelector("p");
  const countLabel = document.querySelector("#search-result .content__tag-txt");
  const countLabelDefault = countLabel ? countLabel.textContent.trim() : "";

  const shell = document.createElement("div");
  shell.className = "mx-search";

  const listEl = document.createElement("div");
  listEl.className = "mx-search__list";

  const moreWrap = document.createElement("div");
  moreWrap.className = "mx-search__more-wrap";
  moreWrap.hidden = true;

  const moreEl = cleanClone(button);
  moreEl.setAttribute("href", "#");
  const moreText = moreEl.querySelector(".elem__button-txt");
  if (moreText) moreText.textContent = t.loadMore;
  moreWrap.appendChild(moreEl);

  shell.appendChild(listEl);
  shell.appendChild(moreWrap);
  resultsRoot.insertBefore(shell, emptyWrap || null);

  let debounceTimer = null;
  let requestId = 0;
  let currentQuery = "";
  let currentPage = 0;
  let totalPages = 0;

  const toggleEmpty = (show, title, body) => {
    if (!emptyWrap) return;

    emptyWrap.style.display = show ? "" : "none";
    if (show && emptyTitle && title) emptyTitle.textContent = title;
    if (show && emptyBody && body) emptyBody.textContent = body;
  };

  const setCount = (nbHits) => {
    if (!countLabel) return;
    countLabel.textContent = nbHits == null ? countLabelDefault : t.results(nbHits);
  };

  const syncUrl = (query) => {
    const url = new URL(window.location.href);
    url.search = "";
    if (query) url.searchParams.set("q", query);
    window.history.replaceState(null, "", url.toString());
  };

  const appendHits = (hits) => {
    const fragment = document.createDocumentFragment();
    hits.forEach((hit) =>
      fragment.appendChild(fillCard(cleanClone(card), hit, { ctaLabel: t.learnMore }))
    );
    listEl.appendChild(fragment);
  };

  const reset = () => {
    listEl.innerHTML = "";
    moreWrap.hidden = true;
    currentPage = 0;
    totalPages = 0;
  };

  const dispatch = async (query, page) => {
    const id = (requestId += 1);
    shell.dataset.state = "loading";

    try {
      const result = await search({ query, hitsPerPage: HITS_PER_PAGE, page, locale });
      if (id !== requestId) return;

      shell.dataset.state = "";
      const hits = (result && result.hits) || [];

      // reset() zeroes currentPage and totalPages, so it has to run before they are set
      // from the response — otherwise every first page reported "no further pages" and the
      // load-more button stayed hidden with its click guard permanently closed.
      if (page === 0) reset();

      totalPages = (result && result.nbPages) || 0;
      currentPage = page;

      if (!hits.length && page === 0) {
        toggleEmpty(true, t.emptyTitle, t.emptyBody);
        setCount(0);
        return;
      }

      toggleEmpty(false);
      appendHits(hits);
      moreWrap.hidden = currentPage >= totalPages - 1;
      setCount(result ? result.nbHits : 0);
    } catch (error) {
      if (id !== requestId) return;

      shell.dataset.state = "";
      console.warn("[mx-search] Search request failed", error);
    }
  };

  const applyQuery = (query, { immediate = false } = {}) => {
    currentQuery = query;
    document.body.classList.toggle("search-has-query", Boolean(query));
    syncUrl(query);

    window.clearTimeout(debounceTimer);

    if (!query) {
      requestId += 1;
      reset();
      toggleEmpty(true, t.promptTitle, t.prompt);
      setCount(null);
      return;
    }

    if (immediate) dispatch(query, 0);
    else debounceTimer = window.setTimeout(() => dispatch(query, 0), DEBOUNCE_MS);
  };

  input.addEventListener("input", (event) => applyQuery(event.target.value.trim()));
  input.addEventListener("search", (event) =>
    applyQuery(event.target.value.trim(), { immediate: true })
  );

  // The button component is an <a>, so suppress the "#" navigation.
  moreEl.addEventListener("click", (event) => {
    event.preventDefault();
    if (currentPage >= totalPages - 1) return;
    dispatch(currentQuery, currentPage + 1);
  });

  const initialQuery = (new URL(window.location.href).searchParams.get("q") || "").trim();
  if (initialQuery) input.value = initialQuery;
  applyQuery(initialQuery, { immediate: true });

  return { applyQuery };
}
